import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";
import logo from "../../images/logo.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const ROAD_TEST_TASK_ID = 69;

const RoadTestForm = () => {
  const navigate = useNavigate();
  const { id: paramId, rtId } = useParams();
  const isNew = !rtId || rtId === "new";

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [record, setRecord] = useState({
    transmission: "",
    total_demerits: "", // ✅ ADD THIS

    disqualified: false,

    dangerous_action: false,
    dangerous_action_reason: "",

    traffic_violation: false,
    traffic_violation_reason: "",

    other_reason: "",
    qualified: "",
    odometer_start: "",
    odometer_end: "",
    pretrip_time: "",
    posttrip_time: "",
  });

  const [driverDisplayName, setDriverDisplayName] = useState("");
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [isSigned, setIsSigned] = useState(false);

  // group items by section for rendering rowSpan like your screenshot
  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const sec = it.rti_section || "General";
      if (!map.has(sec)) map.set(sec, []);
      map.get(sec).push(it);
    }

    // keep stable ordering: General first, then as inserted
    const order = [];
    for (const it of items) {
      const sec = it.rti_section || "General";
      if (!order.includes(sec)) order.push(sec);
    }

    return order.map((sec) => ({ section: sec, rows: map.get(sec) || [] }));
  }, [items]);

  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    // ✅ NEW → load template
    if (isNew) {
      axios
        .get(`${API_BASE}/api/roadtest/template/all`, { headers })
        .then((res) => {
          setItems(res.data || []);
        })
        .catch((err) => {
          console.error("❌ Failed to load template", err);
        });
      return;
    }

    // ✅ EDIT → load existing record
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/roadtest/detail/${rtId}`, {
          headers,
        });

        const { record: r, items: its, files: fs } = res.data || {};
        setItems(its || []);
        setFiles(fs || []);

        setRecord({
          transmission: r?.rt_transmission || "",
          total_demerits: r?.rt_total_demerits || "", // ✅ ADD THIS
          disqualified: !!r?.rt_disqualified,
          dangerous_action: !!r?.rt_dangerous_action,
          traffic_violation: !!r?.rt_traffic_violation,
          dangerous_action_reason: r?.rt_dangerous_action_reason || "",
          traffic_violation_reason: r?.rt_traffic_violation_reason || "",
          other_reason: r?.rt_other_reason || "",
          qualified: r?.rt_qualified || "",
          odometer_start: r?.rt_odometer_start || "",
          odometer_end: r?.rt_odometer_end || "",
          pretrip_time: r?.rt_pretrip_time || "",
          posttrip_time: r?.rt_posttrip_time || "",
          driver_name: r?.rt_driver_name || "",
        });
      } catch (err) {
        console.error("❌ Fetch failed", err);
      }
    };

    fetchDetail();
  }, [isNew, rtId]);

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");

        // ✅ SAME logic as Padlock
        const url = isAdmin
          ? `${API_BASE}/api/users/${userId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // ✅ SAME field names as Padlock
        const { first_name, last_name } = res.data;

        setDriverDisplayName([first_name, last_name].filter(Boolean).join(" "));
      } catch (err) {
        console.error("❌ Failed to fetch user name", err);
      }
    };

    fetchUserName();
  }, [isAdmin, userId]);

  // signature status (same idea as Work Permit)
  useEffect(() => {
    const fetchSignStatus = async () => {
      if (!rtId || rtId === "new") return;

      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const refKey = `69_${rtId}`;
        const taskKey = "road_test";
        const status =
          res.data.status?.[refKey] ??
          res.data.status?.[taskKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("❌ Failed to fetch sign status", err);
      }
    };

    fetchSignStatus();
  }, [userId, rtId]);

  const setAttempt = (rti_id, attemptKey, value) => {
    setItems((prev) =>
      prev.map((it) =>
        it.rti_id === rti_id ? { ...it, [attemptKey]: value } : it,
      ),
    );
  };

  const handleHeaderChange = (key, value) => {
    setRecord((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();

    // header fields
    formData.append("transmission", record.transmission || "");
    formData.append("total_demerits", record.total_demerits || "");
    formData.append("disqualified", record.disqualified ? "1" : "");
    formData.append("dangerous_action", record.dangerous_action ? "1" : "");
    formData.append("traffic_violation", record.traffic_violation ? "1" : "");
    formData.append(
      "dangerous_action_reason",
      record.dangerous_action_reason || "",
    );
    formData.append(
      "traffic_violation_reason",
      record.traffic_violation_reason || "",
    );
    formData.append("other_reason", record.other_reason || "");
    formData.append("qualified", record.qualified || "");
    formData.append("odometer_start", record.odometer_start || "");
    formData.append("odometer_end", record.odometer_end || "");
    formData.append("pretrip_time", record.pretrip_time || "");
    formData.append("posttrip_time", record.posttrip_time || "");

    const payloadItems = items.map((it) => ({
      rti_id: it.rti_id, // still used for UPDATE
      rti_section: it.rti_section,
      rti_title: it.rti_title,
      rti_attempt1: it.rti_attempt1 || null,
      rti_attempt2: it.rti_attempt2 || null,
    }));

    formData.append("items", JSON.stringify(payloadItems));
    formData.append("filesToDelete", JSON.stringify(filesToDelete));

    // multiple files
    for (const f of newFiles) formData.append("files", f);

    try {
      if (isNew) {
        const res = await axios.post(
          `${API_BASE}/api/roadtest?userId=${userId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        alert("✔️ Road Test created successfully!");
        navigate(`/roadtest/${userId}/detail/${res.data.rtId}`);
      } else {
        await axios.put(
          `${API_BASE}/api/roadtest/${rtId}?userId=${userId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        alert("✔️ Road Test updated successfully!");

        // refresh detail to show saved total + new files
        const fresh = await axios.get(
          `${API_BASE}/api/roadtest/detail/${rtId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setFiles(fresh.data.files || []);
        setFilesToDelete([]);
      }

      setNewFiles([]);
    } catch (err) {
      console.error("❌ Save failed", err);
      alert("Save failed. Please try again.");
    }
  };

  const handleDeleteFile = (rtfId) => {
    if (!window.confirm("Remove this file?")) return;

    // mark for deletion
    setFilesToDelete((prev) => [...prev, rtfId]);

    // hide immediately from UI
    setFiles((prev) => prev.filter((f) => f.rtf_id !== rtfId));
  };

  const disabled = !isAdmin && isSigned;

  // hide manual/auto sections based on selection (still stored; just not shown)
  const shouldShowSection = (section) => {
    if (section === "Manual Transmission")
      return record.transmission === "manual";
    if (section === "Auto Transmission") return record.transmission === "auto";
    return true;
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Road Test Driving Evaluation
        </span>
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/roadtest/${userId}`)}
          >
            View List
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        {/* Company header like Work Permit */}
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Road Test Driving Evaluation</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit}>
          {/* Transmission */}
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Transmission</label>
              <div className="d-flex gap-4">
                <label className="d-flex align-items-center gap-2">
                  <input
                    type="radio"
                    name="transmission"
                    checked={record.transmission === "manual"}
                    disabled={disabled}
                    onChange={() =>
                      handleHeaderChange("transmission", "manual")
                    }
                  />
                  Manual
                </label>
                <label className="d-flex align-items-center gap-2">
                  <input
                    type="radio"
                    name="transmission"
                    checked={record.transmission === "auto"}
                    disabled={disabled}
                    onChange={() => handleHeaderChange("transmission", "auto")}
                  />
                  Auto
                </label>
              </div>
            </div>

            <div className="col-md-4">
              <label className="form-label">Total Demerits</label>
              <input
                className="form-control"
                value={record.total_demerits}
                disabled={disabled}
                onChange={(e) =>
                  handleHeaderChange("total_demerits", e.target.value)
                }
              />
              <div className="text-muted" style={{ fontSize: 12 }}>
                Auto-disqualify if &gt; 60 demerits
              </div>
            </div>
          </div>

          {/* SECTION 1: Big table */}
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "22%" }}></th>
                  <th style={{ width: "38%" }}></th>
                  <th style={{ width: "8%" }} className="text-center">
                    Demerit
                  </th>
                  <th colSpan={4} className="text-center">
                    Attempt
                  </th>
                </tr>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th colSpan={2} className="text-center">
                    1st
                  </th>
                  <th colSpan={2} className="text-center">
                    2nd
                  </th>
                </tr>
              </thead>

              <tbody>
                {grouped
                  .filter((g) => shouldShowSection(g.section))
                  .map((g) => {
                    const rowSpan = g.rows.length;
                    return g.rows.map((row, idx) => (
                      <tr key={row.rti_id}>
                        {idx === 0 ? (
                          <td rowSpan={rowSpan} style={{ fontWeight: 600 }}>
                            {g.section}
                          </td>
                        ) : null}

                        <td>{row.rti_title}</td>
                        <td className="text-center">{row.rti_demerit}</td>

                        {/* Attempt 1 Yes */}
                        <td className="text-center">
                          <label className="d-flex justify-content-center gap-2">
                            Yes
                            <input
                              type="radio"
                              name={`a1_${row.rti_id}`}
                              checked={row.rti_attempt1 === "yes"}
                              disabled={disabled}
                              onChange={() =>
                                setAttempt(row.rti_id, "rti_attempt1", "yes")
                              }
                            />
                          </label>
                        </td>

                        {/* Attempt 1 No */}
                        <td className="text-center">
                          <label className="d-flex justify-content-center gap-2">
                            No
                            <input
                              type="radio"
                              name={`a1_${row.rti_id}`}
                              checked={row.rti_attempt1 === "no"}
                              disabled={disabled}
                              onChange={() =>
                                setAttempt(row.rti_id, "rti_attempt1", "no")
                              }
                            />
                          </label>
                        </td>

                        {/* Attempt 2 Yes */}
                        <td className="text-center">
                          <label className="d-flex justify-content-center gap-2">
                            Yes
                            <input
                              type="radio"
                              name={`a2_${row.rti_id}`}
                              checked={row.rti_attempt2 === "yes"}
                              disabled={disabled}
                              onChange={() =>
                                setAttempt(row.rti_id, "rti_attempt2", "yes")
                              }
                            />
                          </label>
                        </td>

                        {/* Attempt 2 No */}
                        <td className="text-center">
                          <label className="d-flex justify-content-center gap-2">
                            No
                            <input
                              type="radio"
                              name={`a2_${row.rti_id}`}
                              checked={row.rti_attempt2 === "no"}
                              disabled={disabled}
                              onChange={() =>
                                setAttempt(row.rti_id, "rti_attempt2", "no")
                              }
                            />
                          </label>
                        </td>
                      </tr>
                    ));
                  })}
              </tbody>
            </table>
          </div>

          {/* SECTION 2: Disqualification + Qualified (your screenshot 3) */}
          <div className="mt-4">
            <div style={{ fontWeight: 700 }}>*Disqualification</div>
            <div className="text-muted mb-2">
              Vehicle Road Test exceeds 60 demerits
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    checked={record.dangerous_action}
                    disabled={disabled}
                    onChange={(e) =>
                      handleHeaderChange("dangerous_action", e.target.checked)
                    }
                  />
                  Dangerous Action
                </label>
              </div>
              <div className="col-md-8">
                <input
                  className="form-control"
                  value={record.dangerous_action_reason}
                  disabled={disabled || !record.dangerous_action}
                  onChange={(e) =>
                    handleHeaderChange(
                      "dangerous_action_reason",
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    checked={record.traffic_violation}
                    disabled={disabled}
                    onChange={(e) =>
                      handleHeaderChange("traffic_violation", e.target.checked)
                    }
                  />
                  Traffic Violation
                </label>
              </div>
              <div className="col-md-8">
                <input
                  className="form-control"
                  value={record.traffic_violation_reason}
                  disabled={disabled || !record.traffic_violation}
                  onChange={(e) =>
                    handleHeaderChange(
                      "traffic_violation_reason",
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!record.other_reason}
                    disabled={disabled}
                    onChange={(e) => {
                      if (!e.target.checked)
                        handleHeaderChange("other_reason", "");
                      else
                        handleHeaderChange(
                          "other_reason",
                          record.other_reason || " ",
                        );
                    }}
                  />
                  Other (specify)
                </label>
              </div>
              <div className="col-md-8">
                <input
                  className="form-control"
                  value={record.other_reason}
                  disabled={disabled || !record.other_reason}
                  onChange={(e) =>
                    handleHeaderChange("other_reason", e.target.value)
                  }
                />
              </div>
            </div>

            <div style={{ fontWeight: 700 }} className="mt-4">
              *Qualified
            </div>

            <div className="d-flex gap-4 mb-3">
              <label className="d-flex align-items-center gap-2">
                <input
                  type="radio"
                  name="qualified"
                  checked={record.qualified === "yes"}
                  disabled={disabled}
                  onChange={() => handleHeaderChange("qualified", "yes")}
                />
                Yes
              </label>

              <label className="d-flex align-items-center gap-2">
                <input
                  type="radio"
                  name="qualified"
                  checked={record.qualified === "no"}
                  disabled={disabled}
                  onChange={() => handleHeaderChange("qualified", "no")}
                />
                No
              </label>

              <label className="d-flex align-items-center gap-2">
                <input
                  type="radio"
                  name="qualified"
                  checked={record.qualified === "discontinued"}
                  disabled={disabled}
                  onChange={() =>
                    handleHeaderChange("qualified", "discontinued")
                  }
                />
                Discontinued
              </label>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Odometer Ending:</label>
                <input
                  className="form-control"
                  value={record.odometer_end}
                  disabled={disabled}
                  onChange={(e) =>
                    handleHeaderChange("odometer_end", e.target.value)
                  }
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Odometer Starting:</label>
                <input
                  className="form-control"
                  value={record.odometer_start}
                  disabled={disabled}
                  onChange={(e) =>
                    handleHeaderChange("odometer_start", e.target.value)
                  }
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Pre-Trip start time:</label>
                <input
                  className="form-control"
                  value={record.pretrip_time}
                  disabled={disabled}
                  onChange={(e) =>
                    handleHeaderChange("pretrip_time", e.target.value)
                  }
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Post-trip end time:</label>
                <input
                  className="form-control"
                  value={record.posttrip_time}
                  disabled={disabled}
                  onChange={(e) =>
                    handleHeaderChange("posttrip_time", e.target.value)
                  }
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Driver Name:</label>
                <input
                  className="form-control"
                  style={{ maxWidth: "280px" }}
                  value={driverDisplayName}
                  disabled
                />
              </div>
            </div>
          </div>

          <hr className="my-5" />

          {/* Uploads (multi-file) */}
          <div className="mb-3">
            <label className="form-label">Upload Files (PDF/JPG/PNG)</label>
            <input
              type="file"
              className="form-control"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={disabled}
              onChange={(e) => setNewFiles(Array.from(e.target.files || []))}
            />
          </div>

          {/* Existing files list */}
          {files?.length > 0 && (
            <UploadedAttachmentList
              title="Uploaded Files"
              files={files}
              getFileKey={(file) => file.rtf_id}
              getFileName={(file) => file.rtf_original}
              getFileViewUrl={(file) => `${API_BASE}/uploads/roadtests/${file.rtf_file}`}
              getFileDownloadName={(file) => file.rtf_original}
              onDelete={!disabled ? (file) => handleDeleteFile(file.rtf_id) : null}
            />
          )}

          {/* Save */}
          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary mt-4"
              disabled={disabled}
            >
              Save
            </button>
          </div>
        </form>

        {/* Signature & Review (same as Work Permit pattern) */}
        {!isNew && (
          <>
            <hr className="my-4" />
            <SignatureManager
              taskName="road_test"
              taskId={ROAD_TEST_TASK_ID}
              referenceId={rtId}
              userId={userId}
              isAdmin={isAdmin}
            />

            <ReviewManager
              taskName="road_test"
              taskId={ROAD_TEST_TASK_ID}
              referenceId={rtId}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default RoadTestForm;


